import React, { useState } from 'react';
import { MapPin, Send, MessageCircle, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useToast } from '../hooks/use-toast';
import { PageSEO } from './SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration contact
const WHATSAPP_NUMBER = '33640486009';
const PHONE_NUMBER = '+33640486009';
const WHATSAPP_LINK = 'https://wa.me/33640486009?text=Bonjour%2C%20je%20souhaite%20prendre%20rendez-vous%20%F0%9F%92%85';

// Zones courantes à La Réunion
const ZONES = [
  'Ravine des Cabris',
  'Saint-Pierre',
  'Le Tampon',
  'Saint-Louis',
  'Saint-Leu',
  'Les Avirons',
  'Étang-Salé',
  'Petite-Île',
  'Saint-Joseph',
  'Autre (préciser dans le message)'
];

export const Contact = () => {
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    zone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Envoyer au backend
      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: formData.phone,
        email: '',
        date: new Date().toISOString(),
        service: `Zone: ${formData.zone || 'Non précisée'}`,
        message: formData.message,
        zone: formData.zone || 'non_precisee'
      });
      
      toast({ 
        title: '✅ Message envoyé !', 
        description: 'Je vous recontacte très vite.' 
      });
      
      setFormData({ name: '', phone: '', zone: '', message: '' });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: 'Veuillez réessayer ou me contacter sur WhatsApp.', 
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    window.open(WHATSAPP_LINK, '_blank');
  };

  return (
    <section id="contact" className="py-16 md:py-20" style={{ backgroundColor: '#E8B4D1' }}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-3">
            Contact
          </h2>
          <p className="text-base md:text-lg text-gray-700 max-w-xl mx-auto">
            Une question ? Contactez-moi directement
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Boutons d'action rapide */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center justify-center gap-2 py-4 bg-gray-100 hover:bg-gray-200 rounded-2xl text-gray-800 font-semibold transition-colors min-h-[56px]"
            >
              <Phone size={22} />
              <span>Appeler</span>
            </a>
            <button
              onClick={openWhatsApp}
              className="flex items-center justify-center gap-2 py-4 bg-green-500 hover:bg-green-600 rounded-2xl text-white font-semibold transition-colors min-h-[56px]"
            >
              <MessageCircle size={22} />
              <span>WhatsApp</span>
            </button>
          </div>

          {/* Formulaire simplifié */}
          <Card className="shadow-lg bg-white" style={{ borderWidth: '2px', borderColor: '#E8B4D1' }}>
            <CardHeader className="pb-4">
              <CardTitle className="text-xl md:text-2xl text-center">
                Ou laissez-moi un message
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nom */}
                <div>
                  <Label htmlFor="name" className="text-base font-medium">
                    Nom <span style={{ color: '#D1477A' }}>*</span>
                  </Label>
                  <Input 
                    id="name" 
                    name="name" 
                    type="text"
                    autoComplete="name"
                    value={formData.name} 
                    onChange={handleChange} 
                    required 
                    className="mt-1.5 h-12 text-base rounded-xl"
                    placeholder="Votre nom"
                    aria-required="true"
                  />
                </div>

                {/* Téléphone/WhatsApp */}
                <div>
                  <Label htmlFor="phone" className="text-base font-medium">
                    Téléphone / WhatsApp <span style={{ color: '#D1477A' }}>*</span>
                  </Label>
                  <Input 
                    id="phone" 
                    name="phone" 
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    value={formData.phone} 
                    onChange={handleChange} 
                    required 
                    className="mt-1.5 h-12 text-base rounded-xl"
                    placeholder="06 92 12 34 56"
                    aria-required="true"
                  />
                </div>

                {/* Zone (optionnel) */}
                <div>
                  <Label htmlFor="zone" className="text-base font-medium">
                    Zone <span className="text-gray-400 font-normal">(optionnel)</span>
                  </Label>
                  <select
                    id="zone"
                    name="zone"
                    value={formData.zone}
                    onChange={handleChange}
                    className="mt-1.5 w-full h-12 px-3 text-base border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': '#D1477A' }}
                  >
                    <option value="">Sélectionnez votre zone</option>
                    {ZONES.map((zone) => (
                      <option key={zone} value={zone}>{zone}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-base font-medium">
                    Message <span style={{ color: '#D1477A' }}>*</span>
                  </Label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    value={formData.message} 
                    onChange={handleChange} 
                    rows={4} 
                    required
                    className="mt-1.5 text-base rounded-xl resize-none"
                    placeholder="Précisez la prestation souhaitée, vos disponibilités..."
                  />
                </div>

                {/* Bouton envoyer */}
                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full text-white py-4 h-14 rounded-xl text-base font-semibold gap-2"
                  style={{ backgroundColor: '#D1477A' }}
                >
                  <Send size={20} />
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Infos pratiques */}
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <a
              href="https://maps.google.com/?q=Ravine+des+Cabris+La+Reunion"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 bg-white/80 rounded-2xl hover:bg-white transition-colors"
            >
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <MapPin style={{ color: '#D1477A' }} size={24} />
              </div>
              <div>
                <p className="font-semibold text-gray-800">Service à domicile</p>
                <p className="text-sm text-gray-600">Ravine des Cabris, La Réunion</p>
              </div>
            </a>
            <div className="flex items-center gap-3 p-4 bg-white/80 rounded-2xl">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-2xl">🕐</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Horaires</p>
                <p className="text-sm text-gray-600">Lun-Sam • 8h-18h</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
